import { Button } from '@creditornot/cb-button';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { deleteMerchantAppLogin } from 'apis/restaurant-api/merchant-admin/venue/api';
import { APIErrorNotification, WoltModal } from 'common/components';

import { stripMerchantAppLoginPrefix } from '../utils';

type Props = {
  isOpen: boolean;
  venueId: string;
  username: string;
  onClose: () => void;
  onDone: () => void;
};

const Root = styled(WoltModal)`
  width: 100%;
  max-width: 480px;
  background: ${cbSemanticCssVars.surfaceMain};
  border-radius: 8px;
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const Actions = styled.div`
  display: flex;
`;

const StyledApiErrorNotification = styled(APIErrorNotification)`
  width: 100%;
  margin-block-end: 16px;
`;

const PasswordDeleteModal = ({ isOpen, venueId, username, onClose, onDone }: Props) => {
  const { mutate, isLoading, error, reset } = useMutation(() => deleteMerchantAppLogin(venueId, username), {
    onSuccess: () => {
      onDone();
      handleClose();
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Root isOpen={isOpen} onRequestClose={handleClose}>
      <ModalHeader onCloseClick={handleClose}>
        <FormattedMessage id="userManagement.deleteCredentialsHeader" />
      </ModalHeader>
      <ModalContent>
        <FormattedMessage
          id="userManagement.deleteCredentialsBody"
          values={{
            strong: chunks => <strong>{chunks}</strong>,
            username: stripMerchantAppLoginPrefix(username),
          }}
        />
      </ModalContent>
      <StyledModalFooter>
        {!!error && <StyledApiErrorNotification error={error} />}
        <Actions>
          <Button variant="lightBlue" size="small" onClick={handleClose} disabled={isLoading}>
            <FormattedMessage id="state.cancel" />
          </Button>
          <Button
            style={{ marginInlineStart: 16 }}
            variant="lightRed"
            size="small"
            onClick={() => mutate()}
            disabled={isLoading}
            loading={isLoading}
          >
            <FormattedMessage id="state.delete" />
          </Button>
        </Actions>
      </StyledModalFooter>
    </Root>
  );
};

export default PasswordDeleteModal;
